<template>
  <v-container fluid class="registration-confirmation mt-3" v-if="authorized">
    <v-row justify="center" class="pa-5 pa-md-0 blue-bg">
      <v-col cols="12" md="10" class="pt-5">
        <h5 class="white--text font-weight-bold i18n" data-i18n="[html]content.body">
          {{ $t('settings/profile:registration.titles.welcome') }}
          &trade;
        </h5>
        <h6 class="white--text font-weight-bold">{{ $t('settings/profile:registration.titles.invitation') }}</h6>
        <h6 class="white--text font-weight-bold">{{ $t('settings/profile:registration.titles.delete-info') }}</h6>
      </v-col>
    </v-row>
    <v-row justify="center" class="pt-5" no-gutters>
      <v-col cols="12" md="5">
        <h5 class="greyMedium--text font-weight-bold mb-0">{{ $t('settings/profile:common.profile') }}</h5>
      </v-col>
      <v-col cols="12" md="5" class="d-none d-md-block"></v-col>
    </v-row>

    <!-- Profile data -->
    <v-form ref="profileForm">
      <v-row justify="center" class="pt-0">
        <v-col cols="12" md="5">
          <!-- Email -->
          <v-text-field
              outlined
              clearable
              v-model="users.email"
              :label="$t('common:email')"
              :rules="[ruleRequired($t('utils:rules.required'))]"
          />

          <!-- Password -->
          <v-text-field
              outlined
              v-model="users.password"
              :append-icon="passwordShow ? 'visibility_off' : 'visibility'"
              :rules="[ruleRequired($t('utils:rules.required')), ruleMinLength(8, $t('utils:rules.min8Characters'))]"
              :type="passwordShow ? 'text' : 'password'"
              :label="$t('settings/profile:common.password')"
              counter
              @click:append="passwordShow = !passwordShow"
          />

          <!-- Password -->
          <v-text-field
              outlined
              v-model="verifiedPassword"
              :append-icon="verifyPasswordShow ? 'visibility_off' : 'visibility'"
              :rules="[ruleIsEqual(users.password, $t('utils:rules.pwd-not-equal'))]"
              :type="verifyPasswordShow ? 'text' : 'password'"
              :label="$t('settings/profile:registration.labels.confirm-password')"
              counter
              @click:append="verifyPasswordShow = !verifyPasswordShow"
          />

          <!-- Phonenumber -->
          <v-text-field
              outlined
              clearable
              v-model="users.phone"
              :label="$t('settings/profile:common.phone')"
          />

          <!-- Name -->
          <v-text-field
              outlined
              v-model="users.name"
              :label="$t('common:name')"
              :rules="[ruleRequired($t('utils:rules.required'))]"
          />
        </v-col>
        <v-col cols="12" md="5">
          <v-select
              :items="languages"
              v-model="users.language"
              outlined
              :menu-props="{ bottom: true, offsetY: true }"
              :label="$t('settings/profile:common.language')"
              @change="switchLocale"
          />

          <h6 class="mb-0">{{ $t('settings/profile:common.communication') }}:</h6>
          <v-checkbox
              color="secondary"
              v-model="users.mailNotification"
              :label="$t('settings/profile:common.mail-notifications')"
              class="mt-0">
          </v-checkbox>
        </v-col>
      </v-row>
    </v-form>

    <v-form ref="customerForm" v-if="!users.customer">
      <v-row justify="center" class="pt-5" no-gutters>
        <v-col cols="12" md="5">
          <h5 class="greyMedium--text font-weight-bold mb-0">{{ $t('settings/profile:registration.titles.company') }}</h5>
        </v-col>
        <v-col cols="12" md="5" class="d-none d-md-block"></v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" md="5" class="py-0">
          <h6>{{ $t('settings/profile:common.contact') }}</h6>

          <!-- Name -->
          <DuplicateCheckField
              ref="duplName"
              :value="customer.name"
              :label="$t('common:name')"
              required
              item-text="name"
              @input="customer.name = $event"
              @checkForDuplicates="checkByName"
              hide-show-btn
          />
        </v-col>
        <v-col cols="12" md="5" class="py-0">
          <h6>{{ $t('common:address') }}</h6>

          <!-- Street -->
          <DuplicateCheckField
              ref="duplAddress"
              :value="customer.street"
              :label="$t('common:street')"
              required
              item-text="name"
              @input="customer.street = $event"
              @checkForDuplicates="checkByAddress"
              hide-show-btn
          />
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" md="5" class="py-0">
          <!-- Contact Name -->
          <v-text-field
              outlined
              clearable
              v-model="customer.contactName"
              :label="$t('settings/profile:registration.labels.company-owner')"
              :rules="[ruleRequired($t('utils:rules.required'))]"
          />
        </v-col>
        <v-col cols="12" md="5" class="py-0">
          <v-row>
            <v-col cols="12" md="6" class="py-0">
              <!-- ZIP -->
              <v-text-field
                  outlined
                  clearable
                  v-model="customer.zip"
                  :label="$t('common:zip')"
                  @input="getCityProvinceByZIP"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
              />
            </v-col>
            <v-col cols="12" md="6" class="py-0">
              <!-- City -->
              <v-text-field
                  outlined
                  clearable
                  v-model="customer.city"
                  :label="$t('common:city')"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" md="5" class="py-0">
          <!-- Contact Mail -->
          <v-text-field
              outlined
              clearable
              v-model="customer.contactMail"
              :label="$t('common:email')"
              :rules="[ruleRequired($t('utils:rules.required'))]"
          />
        </v-col>
        <v-col cols="12" md="5" class="py-0">
          <v-row>
            <v-col cols="12" md="6" class="py-0">
              <!-- Country -->
              <v-select
                  outlined
                  clearable
                  v-model="customer.country"
                  :label="$t('common:country')"
                  :menu-props="{ bottom: true, offsetY: true}"
                  :items="countries"
                  item-text="translatedName"
                  item-value="id"
                  @change="updateProvince"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
              />
            </v-col>
            <v-col cols="12" md="6" class="py-0">
              <!-- Province -->
              <v-select
                  outlined
                  v-model="customer.province"
                  :label="$t('common:province')"
                  :items="provinces"
                  item-text="translatedName"
                  item-value="id"
                  :menu-props="{ bottom: true, offsetY: true}"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" md="5" class="py-0">
          <!-- Contact Phone -->
          <v-text-field
              outlined
              clearable
              v-model="customer.contactPhone"
              :label="$t('settings/profile:common.phone')"
              :rules="[ruleRequired($t('utils:rules.required'))]"
          />
        </v-col>
        <v-col cols="12" md="5" class="py-0"></v-col>
      </v-row>
    </v-form>

    <v-row justify="center" class="mb-3">
      <v-col cols="12" md="10" class="pt-5">
        <BaseButton
            color="primary"
            :btn-text="$t('settings/profile:registration.btns.finish-registration')"
            @clickedButton="submitRegistry"
        />
      </v-col>
    </v-row>

    <v-row class="blue-bg mb-3" justify="center">
      <v-col cols="12" md="10" class="pt-5">
        <h6 class="white--text font-weight-bold">{{ $t('settings/profile:registration.titles.delete') }}</h6>

        <TwoButtonDialog
            ref="declineRegistry"
            :btn-text="$t('settings/profile:registration.btns.decline-registration')"
            color="scaleRed"
            :dialog-title="$t('settings/profile:registration.dialog.title')"
            :dialog-text="$t('settings/profile:registration.dialog.text')"
            :button1-text="$t('buttons:cancel')"
            :button2-text="$t('settings/profile:registration.dialog.btn-2')"
            @clickedButton1="$refs.declineRegistry.showDialog = false"
            @clickedButton2="declineRegistry()"
        />
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import Users from "@/modules/core/data/users";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import {ruleRequired, ruleMinLength, ruleIsEqual} from "@/modules/core/services/rules";
import CountryService from "@/modules/core/services/country-service";
import Utils from "@/modules/core/services/utils";
import GPSUtils from "@/modules/core/services/gps-utils";
import RegistrationService from "@/modules/settings/services/registration-service";
import DataUtils from "@/modules/core/services/data-utils";
import i18next from "i18next";
import CustomerRegistration from "@/modules/settings/data/Customer-registration";
import {mapGetters} from 'vuex';
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import DuplicateCheckField from "@/modules/core/components/DuplicateCheckField";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";

export default {
  name: "RegistrationConfirmation",
  components: {DuplicateCheckField, TwoButtonDialog, BaseButton},
  data() {
    return {
      users: new Users(),
      customer: new CustomerRegistration(),
      countries: [],
      provinces: [],
      passwordShow: false,
      verifyPasswordShow: false,
      authorized: false,
      verifiedPassword: null
    }
  },
  computed: {
    languages() {
      return [
        {value: "GERMAN", text: this.$t('enums:languages.german')},
        {value: "ENGLISH", text: this.$t('enums:languages.english')},
      ]
    },

    currentLang() {
      return this.$store.state.translation.language;
    },

    ...mapGetters('auth', ['isLoggedIn'])
  },
  methods: {
    ruleRequired,
    ruleMinLength,
    ruleIsEqual,

    updateProvince() {
      if(!this.customer.country) {
        return;
      }
      CountryService.readProvincesOfCountry(this.customer.country,
          (resp) => {
            this.provinces = resp.data;
            this.customer.province = this.provinces[0].id;
            Utils.translateAllProvinces(this.countries, this.provinces, this.customer.country);
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/profile:customer.settings.snackbar.province')}))
            this.$logger.error(error);
          }
      )
    },

    translateProvinces() {
      for(var i = 0; i < this.countries.length; i++) {
        if(this.countries[i].id == this.customer.country) {
          for(var j = 0; j < this.provinces.length; j++) {
            this.provinces[j].translatedName = Utils.translateProvinces(this.countries[i].name, this.provinces[j].name)
          }
        }
      }
    },

    /**
     * Reads and sets the building city and province
     * by the entered ZIP
     */
    getCityProvinceByZIP() {
      // check if there is a zip and country set for the building
      if (this.customer.zip && this.customer.country) {

        const countryName = GPSUtils.getCountryNameById(this.customer.country, this.countries);

        if (GPSUtils.isValidZIP(countryName.toLowerCase(), this.customer.zip)) {
          GPSUtils.readCityProvinceByZIP(
              this.customer.zip,
              countryName,
              this.$store.state.translation.language,
              this.provinces,
              (res) => {
                this.customer.city = res.city;
                this.customer.province = res.province;
              },
              () => {
                Utils.showFailureSnackbar(this.$t('settings/profile:common.zip-to-city-fail'))
              }
          )
        }
      }
    },

    checkFailedRegistry(error) {
      // if the passed token does not match with the token from the user to activate
      if (error.response.status === 403) {
        this.$router.push('/403')
      } else if (error.response.status === 404) {
        this.$router.push('/404')
      } else {
        Utils.showFailureSnackbar(this.$t('settings/profile:registration.snackbar.failed-registry'));
      }

      this.$logger.error("User could not be activated because: " + error);
    },

    submitRegistry() {
      if (!this.$refs.profileForm.validate()) {
        return;
      }

      if (this.users.customer) {
        // just update the users data and set user to active
        RegistrationService.activateProfile(this.users.id, this.$route.params.confirmationToken, this.users,
            () => {
              window.location.replace("/home");
            },
            (serviceName, error) => {
              this.checkFailedRegistry(error);
            }
        )
      } else {
        if (!this.$refs.customerForm.validate()) {
          return;
        }

        // update the user data and add a new customer
        this.customer.initialUser = this.users;
        RegistrationService.activateProfileWithCustomer(this.customer.initialUser.id, this.$route.params.confirmationToken, this.customer,
            () => {
              window.location.replace("/home");
            },
            (serviceName, error) => {
              this.checkFailedRegistry(error);
            }
        )
      }
    },

    switchLocale() {
      if (this.users.language) {
        let locale;
        if (this.users.language === 'GERMAN') {
          locale = 'de'
        } else {
          locale = 'en'
        }

        this.$store.commit('translation/storeLanguage', locale)
        this.$store.commit('translation/loadNamespaces')
        i18next.changeLanguage(locale)
      }
    },

    declineRegistry() {
      RegistrationService.declineRegistry(this.users.id, this.$route.params.confirmationToken,
          () => {
            this.$refs.declineRegistry.showDialog = false
            window.location.href = "https://www.view-elevator.at/";
          },
          (serviceName, error) => {
            this.$refs.declineRegistry.showDialog = false
            this.$router.push('/404')
            this.$logger.error(serviceName + "User could not be deleted because: " + error);
          }
      )
    },

    checkByName(term) {
      DuplicateCheckService.checkCustomers('NAME', term,
          (res) => {
            this.$refs.duplName.fillData(res.data);
          }
      )
    },

    checkByAddress(term) {
      DuplicateCheckService.checkCustomers('ADDRESS', term,
          (res) => {
            this.$refs.duplAddress.fillData(res.data);
          }
      )
    }
  },
  watch: {
    currentLang(lang) {
      if (!this.users.customer) {
        Utils.sleep(500).then(() => {
          Utils.translateAllCountries(this.countries)
          Utils.translateAllProvinces(this.countries, this.provinces, this.customer.country);
          this.$refs.customerForm.resetValidation()
        });
      }

      if (lang === 'de') {
        this.users.language = 'GERMAN';
      } else {
        this.users.language = 'ENGLISH';
      }
    },
  },
  created() {
    if (this.isLoggedIn) {
      this.$router.push('/404');
    }

    RegistrationService.confirmToken(this.$route.params.confirmationToken,
        (res) => {
          this.users = DataUtils.parseDataToSingleObject(res.data, Users);

          this.switchLocale();

          if (!this.users.customer) {
            CountryService.readAllFromAPI(
                (res) => {
                  this.countries = res.data;
                  Utils.translateAllCountries(this.countries)

                  this.customer.country = this.countries[0].id

                  CountryService.readProvincesOfCountry(this.customer.country,
                      (resp) => {
                        this.provinces = resp.data;
                        Utils.translateAllCountries(this.countries)
                        Utils.translateAllProvinces(this.countries, this.provinces, this.customer.country);
                        this.customer.province = this.provinces[0].id;
                        this.authorized = true;
                      },
                      (error) => {
                        Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:province_plural')}))
                        this.$logger.error(error);
                      }
                  )
                },
                (error) => {
                  Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: 'Länder'}))
                  this.$logger.error(error);
                }
            )
          } else {
            this.authorized = true;
          }
        },
        (error) => {
          this.$logger.error("Failed to confirm confirmation token, because: " + error);
          this.$router.push('/403')
        }
    )
  }
}
</script>

<style scoped lang="scss">
.registration-confirmation {
  .blue-bg {
    background-color: #1cb1eb;
  }
}
</style>